import React, { useState, useEffect } from 'react';
import { X, Search, Plus, Car } from 'lucide-react';
import axios from '../../api/axios';
import toast from 'react-hot-toast';

const VehicleSelector = ({ customerId, onSelect, onClose }) => {
  const [tab, setTab] = useState('existing'); // 'existing' | 'new'
  const [allVehicles, setAllVehicles] = useState([]);
  const [brands, setBrands] = useState([]);
  const [models, setModels] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // New vehicle form
  const [newVehicle, setNewVehicle] = useState({
    brandId: '',
    modelId: '',
    plateNumber: '',
    vin: '',
    year: '',
    color: ''
  });

  useEffect(() => {
    fetchVehicles();
    fetchBrands();
  }, [customerId]);

  useEffect(() => {
    if (newVehicle.brandId) fetchModels(newVehicle.brandId);
    else setModels([]);
  }, [newVehicle.brandId]);

  const fetchVehicles = async () => {
    setLoading(true);
    try {
      // Fetch all vehicles from master (no customerId filter) with high limit
      const res = await axios.get('/api/vehicles?limit=1000');
      setAllVehicles(res.data.vehicles || []);
    } catch {
      toast.error('Failed to load vehicles');
    } finally {
      setLoading(false);
    }
  };

  const fetchBrands = async () => {
    try {
      const res = await axios.get('/api/vehicles/brands');
      setBrands(res.data || []);
    } catch {
      toast.error('Failed to load brands');
    }
  };

  const fetchModels = async (brandId) => {
    try {
      const res = await axios.get(`/api/vehicles/models?brandId=${brandId}`);
      setModels(res.data || []);
    } catch {
      toast.error('Failed to load models');
    }
  };

  const filteredVehicles = allVehicles.filter(v => {
    const q = searchTerm.toLowerCase();
    return (
      v.make?.toLowerCase().includes(q) ||
      v.model?.toLowerCase().includes(q) ||
      v.plateNumber?.toLowerCase().includes(q) ||
      v.vin?.toLowerCase().includes(q) ||
      v.customer?.name?.toLowerCase().includes(q)
    );
  });

  const handleSelectExisting = (vehicle) => {
    onSelect({
      id: vehicle.id,
      brand: vehicle.make,
      model: vehicle.model,
      plateNumber: vehicle.plateNumber || '',
      vin: vehicle.vin || '',
      year: vehicle.year || '',
      color: vehicle.color || '',
      isNew: false
    });
  };

  const handleSaveNew = async () => {
    if (!newVehicle.brandId) { toast.error('Please select a brand'); return; }
    if (!newVehicle.modelId) { toast.error('Please select a model'); return; }
    if (!newVehicle.plateNumber.trim()) { toast.error('Plate number is required'); return; }

    setSaving(true);
    try {
      const brand = brands.find(b => b.id === parseInt(newVehicle.brandId));
      const model = models.find(m => m.id === parseInt(newVehicle.modelId));

      const payload = {
        make: brand?.name || '',
        model: model?.name || '',
        brandId: parseInt(newVehicle.brandId),
        modelId: parseInt(newVehicle.modelId),
        plateNumber: newVehicle.plateNumber.trim(),
        vin: newVehicle.vin.trim() || null,
        year: newVehicle.year ? parseInt(newVehicle.year) : null,
        color: newVehicle.color.trim() || null,
        customerId: customerId ? parseInt(customerId) : null
      };

      const res = await axios.post('/api/vehicles', payload);
      const saved = res.data;

      toast.success('Vehicle saved to master');

      // Refresh list
      await fetchVehicles();

      onSelect({
        id: saved.id,
        brand: saved.make,
        model: saved.model,
        plateNumber: saved.plateNumber || '',
        vin: saved.vin || '',
        year: saved.year || '',
        color: saved.color || '',
        isNew: true
      });
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save vehicle');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg w-full max-w-2xl max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className="flex justify-between items-center p-5 border-b">
          <h2 className="text-lg font-semibold">Select Vehicle</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b px-5">
          <button
            onClick={() => setTab('existing')}
            className={`px-4 py-3 text-sm font-medium border-b-2 ${tab === 'existing' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
          >
            Vehicle Master ({allVehicles.length})
          </button>
          <button
            onClick={() => setTab('new')}
            className={`px-4 py-3 text-sm font-medium border-b-2 flex items-center gap-1 ${tab === 'new' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
          >
            <Plus className="w-3 h-3" /> Add New Vehicle
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5">
          {tab === 'existing' ? (
            <div className="space-y-3">
              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Search by make, model, plate, VIN or customer..."
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  className="pl-9 w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                  autoFocus
                />
              </div>

              {loading ? (
                <div className="text-center py-8 text-gray-500 text-sm">Loading vehicles...</div>
              ) : filteredVehicles.length === 0 ? (
                <div className="text-center py-8 text-gray-500 text-sm">
                  {searchTerm ? 'No vehicles match your search.' : 'No vehicles in master yet.'}
                  <button onClick={() => setTab('new')} className="block mx-auto mt-2 text-blue-600 hover:underline text-sm">
                    Add a new vehicle →
                  </button>
                </div>
              ) : (
                <div className="space-y-2">
                  {filteredVehicles.map(vehicle => (
                    <button
                      key={vehicle.id}
                      onClick={() => handleSelectExisting(vehicle)}
                      className="w-full flex items-center gap-3 p-3 border rounded-lg hover:bg-blue-50 hover:border-blue-300 text-left transition-colors"
                    >
                      <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <Car className="w-5 h-5 text-gray-400" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-sm">
                          {vehicle.plateNumber || <span className="text-gray-400 italic">No Plate</span>}
                          {vehicle.vin && <span className="ml-2 text-gray-500 font-normal text-xs">VIN: {vehicle.vin}</span>}
                        </div>
                        <div className="text-xs text-gray-500 mt-0.5">
                          {vehicle.make} {vehicle.model}{vehicle.year ? ` (${vehicle.year})` : ''}{vehicle.color ? ` • ${vehicle.color}` : ''}
                        </div>
                        {vehicle.customer && (
                          <div className="text-xs text-blue-600 mt-0.5">👤 {vehicle.customer.name}</div>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-sm text-gray-500">This vehicle will be saved to the Vehicle Master for future use.</p>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Brand *</label>
                  <select
                    value={newVehicle.brandId}
                    onChange={e => setNewVehicle(p => ({ ...p, brandId: e.target.value, modelId: '' }))}
                    className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                  >
                    <option value="">Select Brand</option>
                    {brands.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Model *</label>
                  <select
                    value={newVehicle.modelId}
                    onChange={e => setNewVehicle(p => ({ ...p, modelId: e.target.value }))}
                    disabled={!newVehicle.brandId}
                    className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm disabled:bg-gray-100"
                  >
                    <option value="">Select Model</option>
                    {models.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Plate Number *</label>
                  <input
                    type="text"
                    value={newVehicle.plateNumber}
                    onChange={e => setNewVehicle(p => ({ ...p, plateNumber: e.target.value }))}
                    placeholder="e.g., ABC-1234"
                    className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">VIN#</label>
                  <input
                    type="text"
                    value={newVehicle.vin}
                    onChange={e => setNewVehicle(p => ({ ...p, vin: e.target.value }))}
                    placeholder="Vehicle Identification Number"
                    className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Year</label>
                  <input
                    type="number"
                    value={newVehicle.year}
                    onChange={e => setNewVehicle(p => ({ ...p, year: e.target.value }))}
                    placeholder="e.g., 2022"
                    min="1900"
                    max={new Date().getFullYear() + 1}
                    className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Color</label>
                  <input
                    type="text"
                    value={newVehicle.color}
                    onChange={e => setNewVehicle(p => ({ ...p, color: e.target.value }))}
                    placeholder="e.g., White"
                    className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                  />
                </div>
              </div>

              <button
                onClick={handleSaveNew}
                disabled={saving}
                className="w-full py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 text-sm font-medium"
              >
                {saving ? 'Saving...' : 'Save & Select Vehicle'}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default VehicleSelector;
